import type { CustomerOrder, Project, Workspace } from '@noconflict/contracts';
import { useTranslation } from 'react-i18next';
import { useOrgContext } from '../../entities/org';
import { CUSTOMER_STATUS_TONE, customerStatus } from '../../entities/project';
import { ReopenOrderButton } from '../../features/order/reopen-order';
import { useFormat } from '../../shared/i18n/format';
import { Card, CardContent, CardHeader, CardTitle } from '../../shared/ui/card';
import { Notice } from '../../shared/ui/notice';
import { StatusBadge } from '../../shared/ui/status-badge';
import { OrderPayControls } from '../../widgets/order-panel';

export type DetailRow = { label: string; value: string };

/**
 * An order from submit on, and a draft for anyone who may not change it: the status (with the pay controls and
 * **Edit order** while it awaits payment, or the due date once it is placed), then the details and choices as a
 * read-only list. Shared by both products; each passes its own rows.
 */
export function OrderDetails({
  project,
  workspace,
  order,
  notice,
  rows,
  wideLabels = [],
}: {
  project: Project;
  workspace: Workspace;
  order: CustomerOrder | null;
  notice: string | null;
  rows: DetailRow[];
  /** Labels of rows that take the full width (long free text). */
  wideLabels?: string[];
}) {
  const { t } = useTranslation(['order', 'project']);
  const { canIn } = useOrgContext();
  const { formatDate, formatDateTime } = useFormat();
  const status = customerStatus(project, order);

  return (
    <div className="grid gap-6">
      {notice ? <Notice>{notice}</Notice> : null}
      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('detail.status')}</h2>
          </CardTitle>
          <StatusBadge tone={CUSTOMER_STATUS_TONE[status]}>
            {t(`project:status.${status}`)}
          </StatusBadge>
        </CardHeader>
        <CardContent className="grid gap-4">
          {status === 'awaiting_payment' && order ? (
            <OrderPayControls
              order={order}
              workspaceId={workspace.id}
              body={t('detail.awaitingBody')}
            >
              {canIn(workspace.id, 'orders.create') && workspace.archived_at === null ? (
                <ReopenOrderButton orderId={order.id} />
              ) : null}
            </OrderPayControls>
          ) : null}
          {status === 'in_progress' || status === 'delivered' ? (
            <div className="grid gap-2">
              {project.due_at ? (
                <p className="font-bold text-heading">
                  {t('detail.dueBy', { date: formatDateTime(project.due_at) })}
                </p>
              ) : null}
              {status === 'in_progress' ? <p>{t('detail.placedBody')}</p> : null}
              {project.ordered_at ? (
                <p className="text-sm text-muted-foreground">
                  {t('detail.placedOn', { date: formatDate(project.ordered_at) })}
                </p>
              ) : null}
            </div>
          ) : null}
          {status === 'cancelled' ? <p>{t('detail.cancelledBody')}</p> : null}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle asChild className="font-sans">
            <h2>{t('detail.title')}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {rows.map((row) => (
              <div
                key={row.label}
                className={wideLabels.includes(row.label) ? 'sm:col-span-2' : ''}
              >
                <dt className="text-sm text-muted-foreground">{row.label}</dt>
                <dd className="break-words whitespace-pre-line text-heading">{row.value}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
